// lib/artifact-navigate.js — ARTIFACT ORIGIN NAVIGATION
// ═══════════════════════════════════════════════════════════
// Single source of truth for "jump to artifact's origin thread".
//
// Given an artifact + the session_type registry, resolves where
// the artifact was born (regular chat, space, workspace) and
// returns navigation instructions.
//
// Actual navigation lives in hooks/use-shell-actions.js — this
// module just resolves intent.
// ═══════════════════════════════════════════════════════════

// Poll-then-scroll helper. Messages each have data-msg-id on the
// DOM (see components/chat-surface/chat-message-list.js). After
// navigation triggers, we poll for the element to appear, then
// scrollIntoView. Gives up after timeoutMs.
export function scrollToMessageWhenLoaded(messageId, { timeoutMs = 3000 } = {}) {
  if (!messageId || typeof window === 'undefined') return;
  const deadline = Date.now() + timeoutMs;
  function tryScroll() {
    const el = document.querySelector(`[data-msg-id="${CSS.escape(messageId)}"]`);
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    if (Date.now() < deadline) requestAnimationFrame(tryScroll);
  }
  requestAnimationFrame(tryScroll);
}

// Resolve the artifact's origin kind + destination. Returns:
//   { kind: 'chat' | 'space' | 'workspace' | 'artifact' | 'unknown',
//     sessionId, sharedSpace? }
//
// Checks sessions first for session_type; if not found, falls back
// to shared_spaces (workspace/space session IDs live there, not in
// sessions). Returns 'unknown' if neither has it.
export async function resolveArtifactOrigin(artifact, supabase) {
  const sessionId = artifact?.session_id;
  if (!sessionId) return { kind: 'unknown', sessionId: null };

  const { data: sessionRow } = await supabase
    .from('sessions')
    .select('id, session_type')
    .eq('id', sessionId)
    .maybeSingle();

  if (sessionRow) {
    const type = sessionRow.session_type;
    if (type === 'workspace' || type === 'space') {
      const { data: space } = await supabase
        .from('shared_spaces')
        .select('*')
        .eq('id', sessionId)
        .maybeSingle();
      return { kind: type, sessionId, sharedSpace: space };
    }
    // 'innovate' kind removed in Phase 4 cleanup. Legacy innovate
    // artifacts now fall through to 'chat' below — they'll open in
    // the regular thread instead of an orphaned panel route.
    if (type === 'artifact') return { kind: 'artifact', sessionId };
    return { kind: 'chat', sessionId };
  }

  const { data: space } = await supabase
    .from('shared_spaces')
    .select('*')
    .eq('id', sessionId)
    .maybeSingle();
  if (space) {
    return { kind: space.is_workspace ? 'workspace' : 'space', sessionId, sharedSpace: space };
  }

  return { kind: 'unknown', sessionId };
}
